import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  getLangFromPath, 
  switchLangPath, 
  getInitialLang,
  getLangFromPathWithFallback,
  LANGUAGES, 
  DEFAULT_LANG 
} from './i18n';

const LanguageContext = createContext(null);

// Get language preference from localStorage
const getStoredLang = () => {
  try {
    return window.localStorage.getItem('twk_lang') || null;
  } catch (e) {
    return null;
  }
};

// Save language preference to localStorage
const saveStoredLang = (lang) => {
  try {
    window.localStorage.setItem('twk_lang', lang);
  } catch (e) {
    console.warn('Could not save language preference:', e);
  }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);
  
  // Get initial language from URL, localStorage, browser, or default
  const initialLang = getLangFromPathWithFallback(location.pathname);
  
  const [currentLang, setCurrentLang] = useState(initialLang);

  // Redirect to preferred language path if on home page and preferred language is not default
  useEffect(() => {
    if (hasRedirectedRef.current) return;
    
    const langFromPath = getLangFromPath(location.pathname);
    const preferredLang = getLangFromPathWithFallback(location.pathname);
    
    // Only redirect from home page ("/") to preferred language home page
    if (location.pathname === "/" && preferredLang !== DEFAULT_LANG) {
      hasRedirectedRef.current = true;
      const newPath = switchLangPath(location.pathname, preferredLang);
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Update language when path changes
  useEffect(() => {
    const langFromPath = getLangFromPath(location.pathname);
    if (LANGUAGES.includes(langFromPath)) {
      setCurrentLang(langFromPath);
    } else {
      // Fallback to stored or browser preference
      const storedLang = getStoredLang();
      if (storedLang && LANGUAGES.includes(storedLang)) {
        setCurrentLang(storedLang);
      }
    }
  }, [location.pathname]);

  // Keep <html lang> in sync so screen readers and search engines get the right language.
  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const changeLanguage = (lang) => {
    if (!LANGUAGES.includes(lang)) {
      console.warn(`Language ${lang} is not supported`);
      return;
    }

    // Save preference
    saveStoredLang(lang);
    
    // Navigate to same page with translated route
    const newLanguagePath = switchLangPath(location.pathname, lang);
    navigate(newLanguagePath, { replace: true });
  };

  const getCurrentPaths = () => {
    // Import getRoutePaths dynamically to avoid circular dependency
    const { getRoutePaths } = require('./i18n');
    return getRoutePaths(currentLang);
  };

  return (
    <LanguageContext.Provider value={{
      currentLang,
      changeLanguage,
      availableLangs: LANGUAGES,
      defaultLang: DEFAULT_LANG,
      getCurrentPaths
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;